<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, Card, EmptyState, PageHeader, SegmentedControl, Spinner, Table } from '@drama/ui'

import { api, getToken, inGuild } from '../api.js'
import { leaderboardPath } from '../leaderboard.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const agentId = String(route.params.id)
const scope = ref('guild')
const period = ref('week')
const rows = ref([])
const loading = ref(false)
const error = ref('')
const ready = ref(false)
let requestId = 0

const scopeOptions = computed(() => [
  { value: 'guild', label: t('leaderboard.scopes.guild') },
  { value: 'world', label: t('leaderboard.scopes.world') },
])
const periodOptions = computed(() => [
  { value: 'week', label: t('leaderboard.periods.week') },
  { value: 'all', label: t('leaderboard.periods.all') },
])
const columns = computed(() => [
  { key: 'rank', label: t('leaderboard.columns.rank'), width: '80px', align: 'right', mono: true },
  { key: 'name', label: t('leaderboard.columns.name'), width: '280px' },
  { key: 'level', label: t('leaderboard.columns.level'), width: '120px', align: 'right', mono: true },
  { key: 'points', label: t('leaderboard.columns.points'), width: '160px', align: 'right', mono: true },
])

async function load() {
  const currentRequest = ++requestId
  loading.value = true
  error.value = ''
  try {
    const result = await api('GET', inGuild(leaderboardPath(agentId, scope.value, period.value)))
    if (currentRequest === requestId) rows.value = result.leaderboard
  } catch (err) {
    if (currentRequest === requestId) error.value = err instanceof Error ? err.message : String(err)
  } finally {
    if (currentRequest === requestId) loading.value = false
  }
}

watch([scope, period], () => {
  if (ready.value) load()
})

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  ready.value = true
  await load()
})
</script>

<template>
  <section class="leaderboard-page">
    <p class="leaderboard-back"><router-link to="/agents">← {{ t('leaderboard.back') }}</router-link></p>
    <PageHeader :title="t('leaderboard.title')" />
    <div class="leaderboard-filters">
      <label>
        <span>{{ t('leaderboard.scope') }}</span>
        <SegmentedControl v-model="scope" :options="scopeOptions" :disabled="loading" :aria-label="t('leaderboard.scope')" />
      </label>
      <label>
        <span>{{ t('leaderboard.period') }}</span>
        <SegmentedControl v-model="period" :options="periodOptions" :disabled="loading" :aria-label="t('leaderboard.period')" />
      </label>
    </div>
    <div v-if="loading" class="leaderboard-loading"><Spinner :label="t('leaderboard.loading')" /></div>
    <EmptyState v-else-if="error" :title="t('leaderboard.error')" :description="error">
      <template #action><Button variant="secondary" @click="load">{{ t('leaderboard.retry') }}</Button></template>
    </EmptyState>
    <EmptyState v-else-if="rows.length === 0" :title="t('leaderboard.empty')" :description="t('leaderboard.emptyHint')" />
    <Card v-else :padded="false">
      <Table :columns="columns" :rows="rows" row-key="agent_id">
        <template #name="{ row }">
          {{ row.name }} <Badge v-if="row.agent_id === agentId" variant="info">{{ t('leaderboard.you') }}</Badge>
        </template>
      </Table>
    </Card>
  </section>
</template>

<style scoped>
.leaderboard-page {
  display: grid;
  gap: var(--drama-space-lg);
}
.leaderboard-back {
  margin: 0;
}
.leaderboard-filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-lg);
}
.leaderboard-filters label {
  display: grid;
  gap: var(--drama-space-sm);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}
.leaderboard-loading {
  display: grid;
  min-height: 12rem;
  place-items: center;
}
</style>
